import { Modal } from '@mantine/core';
import React, { useMemo } from 'react';

import { useDeviceModelFiles } from '@portals/api/partners';
import { AutoFormik } from '@portals/autoformik';
import { ModalProps } from '@portals/framework';
import { useActionDispatch, useOpenModal } from '@portals/redux';
import { SupportedCommandType, FieldType, FieldTypeEnum } from '@portals/types';

import { sendCommand as sendCommandAction } from '../../redux/actions/ticket_device';
import FileSelector from './FileSelector';

interface RunCommandProps
  extends ModalProps<{
    ticketId: string;
    deviceId: string;
    deviceType: string;
    command: SupportedCommandType;
  }> {}

export function RunCommand({
  data: { ticketId, deviceId, deviceType, command },
  closeMe,
}: RunCommandProps) {
  const deviceModelFiles = useDeviceModelFiles(deviceType);
  const openModal = useOpenModal();
  const sendCommand = useActionDispatch(sendCommandAction);

  const {
    name,
    friendly_name,
    file_type,
    description,
    with_file,
    custom_fields,
  } = command;

  const upgrade = (formData: Record<string, any> = {}) => {
    const extra_params = (custom_fields || []).reduce(
      (rc, field) => ({ ...rc, [field.name]: formData[field.name] }),
      {}
    );

    sendCommand(
      ticketId,
      {
        command: name,
        device_id: deviceId,
        file_id: formData.file_id,
        extra_params,
      },
      closeMe
    );
  };

  const modelFiles = useMemo(() => {
    return (deviceModelFiles.data || [])
      .filter((file) => file.type === file_type)
      .sort((a, b) => (a.version > b.version ? -1 : 1));
  }, [deviceModelFiles.data, file_type]);

  const formFields = useMemo(() => {
    let base: FieldType[] = [];

    // Build the command params form
    if (with_file) {
      const fileSelectorProps = {
        files: modelFiles,
        file_type,
        openModal,
        deviceType,
      };

      base.push({
        name: 'file_id',
        title: 'File',
        required: true,
        type: FieldTypeEnum.Custom,
        component: FileSelector,
        inputProps: fileSelectorProps,
      });
    }

    if (custom_fields) {
      base = [...base, ...custom_fields];
    }

    return base;
  }, [custom_fields, deviceType, file_type, modelFiles, openModal, with_file]);

  return (
    <Modal
      opened
      onClose={closeMe}
      title="Send Command"
      sx={(theme) => ({
        '.modal-body': {
          padding: 0,
          marginBottom: theme.spacing.md,
        },
        '.modal-footer': {
          paddingRight: 0,
          paddingLeft: 0,
        },
      })}
    >
      <AutoFormik
        title={friendly_name}
        subTitle={description}
        fields={formFields}
        inProgress={false}
        handleSubmit={upgrade}
        requireChange={false}
        modal={closeMe}
        submitTitle="Send"
        formikProps={{
          validateOnMount: true,
        }}
      />
    </Modal>
  );
}
